'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { safeLocalStorage } from '@/lib/utils';
import { SettingsForm } from '@/components/qr-generator/style/SettingsForm';
import { DynamicQrCode } from '@/components/qr-generator/DynamicQrCode';
import { useGetReservedShortUrlQuery } from '@/lib/api/url-shortener';
import { useHasProPlan } from '@/hooks/useHasProPlan';
import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import { CreatorContentStep } from './CreatorContentStep';
import { CreatorReviewStep } from './CreatorReviewStep';
import { CreatorReviewActions } from './CreatorReviewActions';
import { ExperiencePhonePreview } from './ExperiencePhonePreview';
import { ScannerPreviewFrame } from './ScannerPreviewFrame';
import { CreatorStepIndicator } from './CreatorStepIndicator';
import { CreatorOnboardingTour } from './CreatorOnboardingTour';
import { ExperiencePicker } from './ExperiencePicker';
import { useCreatorWizard } from './use-creator-wizard';
import { parseCreatorDestination, type CreatorExperienceId } from './experience-config';
import { BusinessCardForm } from '@/components/qr-experience/BusinessCardForm';
import { SocialLinksForm } from '@/components/qr-experience/SocialLinksForm';
import { MenuForm } from '@/components/qr-experience/MenuForm';
import { PdfExperienceForm } from '@/components/qr-experience/PdfExperienceForm';
import { CouponForm } from '@/components/qr-experience/CouponForm';
import { AppExperienceForm } from '@/components/qr-experience/AppExperienceForm';
import {
	QrExperienceContentSchema,
	type TBusinessCardExperienceContent,
	type TMenuExperienceContent,
	type TPdfExperienceContent,
	type TCouponExperienceContent,
	type TAppExperienceContent,
	type TQrExperienceContent,
	type TQrExperienceType,
	type TSocialLinksExperienceContent,
} from '@shared/schemas';
import { PublishExperienceButton } from '@/components/qr-experience/PublishExperienceButton';

const READY_EXPERIENCES = new Set<CreatorExperienceId>([
	'website',
	'whatsapp',
	'business-card',
	'social-links',
	'menu',
	'pdf',
	'coupon',
	'wifi',
	'event',
	'vcard',
	'app',
]);

const isLandingExperience = (exp: CreatorExperienceId | null): boolean =>
	exp === 'business-card' ||
	exp === 'social-links' ||
	exp === 'menu' ||
	exp === 'pdf' ||
	exp === 'coupon' ||
	exp === 'app';

export function GuidedQrCreator({ defaultDynamic }: { defaultDynamic: boolean }) {
	const wizard = useCreatorWizard();
	const { content, config, updateContent, updateName, latestQrCode } = useQrCodeGeneratorStore(
		(state) => state,
	);
	const { data: shortUrl } = useGetReservedShortUrlQuery();
	const { hasProPlan } = useHasProPlan();
	const [contentValid, setContentValid] = useState(false);
	const [experienceContent, setExperienceContent] = useState<Partial<TQrExperienceContent>>({});
	const [previewTab, setPreviewTab] = useState<'phone' | 'qr'>('phone');
	const previousSavedCode = useRef(latestQrCode);
	const [hoveredExperience, setHoveredExperience] = useState<CreatorExperienceId | null>(null);

	const activeExperienceType = hoveredExperience || wizard.experience || 'website';

	const onValidityChange = useCallback((valid: boolean) => setContentValid(valid), []);
	const selectExperience = (experience: CreatorExperienceId, advanceStep = false) => {
		if (!READY_EXPERIENCES.has(experience)) return;
		wizard.selectExperience(experience);
		updateName('');

		if (experience === 'social-links') {
			setExperienceContent({
				type: 'social-links',
				headerColor: '#1d4ed8',
				accentColor: '#1d4ed8',
				title: '',
				subtitle: '',
				links: [
					{ platform: 'instagram', url: '', label: 'Instagram' },
					{ platform: 'tiktok', url: '', label: 'TikTok' },
					{ platform: 'youtube', url: '', label: 'YouTube' },
					{ platform: 'website', url: '', label: 'Website' },
				],
			});
		} else if (experience === 'business-card') {
			setExperienceContent({
				type: 'business-card',
				headerColor: '#1d4ed8',
				facilities: ['wifi', 'parking', 'meeting-room', 'refreshments'],
				socialLinks: [],
			});
		} else if (experience === 'menu') {
			setExperienceContent({
				type: 'menu',
				restaurantName: '',
				currency: '$',
				accentColor: '#d97706',
				headerColor: '#d97706',
			});
		} else if (experience === 'pdf') {
			setExperienceContent({
				type: 'pdf',
				title: '',
				pdfUrl: '' as any,
				directPdf: false,
				accentColor: '#0f172a',
				headerColor: '#0f172a',
			});
		} else if (experience === 'coupon') {
			setExperienceContent({
				type: 'coupon',
				company: '',
				title: '',
				couponCode: '',
				accentColor: '#059669',
				headerColor: '#059669',
			});
		} else if (experience === 'app') {
			setExperienceContent({
				type: 'app',
				appName: '',
				accentColor: '#2563eb',
				headerColor: '#2563eb',
			});
		} else {
			setExperienceContent({});
		}

		setPreviewTab('phone');

		if (isLandingExperience(experience)) {
			updateContent({
				type: 'url',
				data: {
					url: `${typeof window !== 'undefined' ? window.location.origin : ''}/p/pending`,
					isDynamic: true,
				},
			});
		} else if (experience === 'wifi') {
			updateContent({
				type: 'wifi',
				data: { ssid: '', password: '', encryption: 'WPA' },
			});
		} else if (experience === 'event') {
			updateContent({
				type: 'event',
				data: {
					title: '',
					startDate: new Date(Date.now() + 3600000).toISOString(),
					endDate: new Date(Date.now() + 7200000).toISOString(),
				},
			});
		} else if (experience === 'vcard') {
			updateContent({
				type: 'vCard',
				data: { isDynamic: defaultDynamic },
			});
		} else {
			updateContent({
				type: 'url',
				data: {
					url: '',
					isDynamic: defaultDynamic,
				},
			});
		}

		setContentValid(false);
		if (advanceStep) {
			wizard.next();
		}
	};

	useEffect(() => {
		if (new URLSearchParams(window.location.search).get('resumeDraft') !== '1') return;
		const serialized = safeLocalStorage.getItem('guidedQrDraft');
		safeLocalStorage.removeItem('guidedQrDraft');
		if (!serialized) return;
		try {
			const draft: unknown = JSON.parse(serialized);
			if (!draft || typeof draft !== 'object') return;
			const value = draft as {
				version?: unknown;
				experience?: unknown;
				name?: unknown;
				savedAt?: unknown;
				content?: unknown;
			};
			if (
				value.version === 2 &&
				isLandingExperience(value.experience as CreatorExperienceId) &&
				typeof value.name === 'string' &&
				typeof value.savedAt === 'number' &&
				Date.now() - value.savedAt <= 60 * 60 * 1000 &&
				Date.now() >= value.savedAt
			) {
				const restored = QrExperienceContentSchema.safeParse(value.content);
				if (!restored.success || restored.data.type !== value.experience) return;
				setExperienceContent(restored.data);
				wizard.resumeAtReview(restored.data.type);
				updateName(value.name.slice(0, 50));
				setContentValid(true);
				return;
			}
			if (
				value.version !== 1 ||
				(value.experience !== 'website' && value.experience !== 'whatsapp') ||
				typeof value.name !== 'string' ||
				value.name.length > 50 ||
				typeof value.savedAt !== 'number' ||
				Date.now() - value.savedAt > 60 * 60 * 1000 ||
				value.savedAt > Date.now() ||
				content.type !== 'url' ||
				!parseCreatorDestination(value.experience, content.data.url)
			)
				return;
			wizard.resumeAtReview(value.experience);
			updateName(value.name);
			setContentValid(true);
		} catch {
			// Invalid or expired drafts are discarded; the validated generator store stays safe.
		}
		// This one-shot runs after the generator provider restores its schema-validated payload.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Saving a dynamic code resets its payload in the existing save flow. Return the user
	// to a clean start after any successful save, including the static-code path.
	useEffect(() => {
		if (previousSavedCode.current === latestQrCode) return;
		previousSavedCode.current = latestQrCode;
		if (wizard.step !== 'review' || !latestQrCode) return;
		safeLocalStorage.removeItem('guidedQrDraft');
		wizard.startOver();
		updateName('');
		updateContent({ type: 'url', data: { url: '', isDynamic: false } });
		setContentValid(false);
		// latestQrCode changes only after a successful save; these actions should run once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latestQrCode, wizard.step, updateContent, updateName]);

	const advance = () => {
		if (wizard.step === 'purpose' && !wizard.experience) return;
		if (wizard.step === 'content' && !contentValid) return;
		wizard.next();
	};

	return (
		<div
			className={`mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 ${wizard.step === 'purpose' ? '' : 'pb-32'}`}
		>
			<div className="flex flex-wrap items-start gap-x-4">
				<CreatorStepIndicator step={wizard.step} />
				{wizard.step !== 'purpose' && <CreatorOnboardingTour experience={wizard.experience} />}
			</div>
			<div
				className={`grid items-start gap-8 min-h-[calc(100vh-14rem)] ${
					isLandingExperience(wizard.experience)
						? 'lg:grid-cols-[minmax(0,1fr)_320px]'
						: 'lg:grid-cols-[minmax(0,1fr)_320px]'
				}`}
			>
				<div className="min-w-0 w-full rounded-2xl border border-slate-200 bg-white p-3.5 sm:p-8 shadow-sm min-h-[calc(100vh-14rem)]">
					{wizard.step === 'purpose' && (
						<ExperiencePicker
							value={wizard.experience}
							onSelect={(exp) => selectExperience(exp, true)}
							onHover={setHoveredExperience}
						/>
					)}
					{wizard.experience && (
						<div hidden={wizard.step !== 'content'}>
							{(wizard.experience === 'website' ||
								wizard.experience === 'whatsapp' ||
								wizard.experience === 'wifi' ||
								wizard.experience === 'event' ||
								wizard.experience === 'vcard') && (
								<CreatorContentStep
									key={wizard.experience}
									experience={wizard.experience}
									defaultDynamic={defaultDynamic}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'business-card' && (
								<BusinessCardForm
									value={experienceContent as Partial<TBusinessCardExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'social-links' && (
								<SocialLinksForm
									value={experienceContent as Partial<TSocialLinksExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'menu' && (
								<MenuForm
									value={experienceContent as Partial<TMenuExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'pdf' && (
								<PdfExperienceForm
									value={experienceContent as Partial<TPdfExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'coupon' && (
								<CouponForm
									value={experienceContent as Partial<TCouponExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
							{wizard.experience === 'app' && (
								<AppExperienceForm
									value={experienceContent as Partial<TAppExperienceContent>}
									onChange={setExperienceContent}
									onValidityChange={onValidityChange}
								/>
							)}
						</div>
					)}
					{wizard.step === 'design' && (
						<section>
							<h1 className="text-2xl font-bold tracking-tight text-slate-950">
								Design your QR code
							</h1>
							<p className="mb-6 mt-2 text-slate-600">
								Customize the look. Keep strong contrast so phones can scan it reliably.
							</p>
							<SettingsForm />
						</section>
					)}
					{wizard.step === 'review' && wizard.experience && (
						<CreatorReviewStep experience={wizard.experience} />
					)}
				</div>
				<aside
					className={`${
						wizard.step === 'review' ? 'hidden' : 'mt-0 block lg:sticky lg:top-24 lg:self-start'
					}`}
				>
					<div className="flex flex-col items-center">
						{/* Sleek Minimal Preview Switcher */}
						<div className="mb-1.5 flex items-center gap-1 bg-slate-100/90 p-0.5 rounded-full border border-slate-200/80 shadow-2xs">
							<button
								type="button"
								onClick={() => setPreviewTab('phone')}
								className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition cursor-pointer ${
									previewTab === 'phone'
										? 'bg-white text-slate-900 shadow-xs'
										: 'text-slate-500 hover:text-slate-800'
								}`}
							>
								📱 Mobile View
							</button>
							<button
								type="button"
								onClick={() => setPreviewTab('qr')}
								className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition cursor-pointer ${
									previewTab === 'qr'
										? 'bg-white text-slate-900 shadow-xs'
										: 'text-slate-500 hover:text-slate-800'
								}`}
							>
								🏁 QR Code
							</button>
						</div>

						{previewTab === 'phone' ? (
							<ExperiencePhonePreview
								experienceContent={experienceContent}
								experienceType={
									activeExperienceType as
										TQrExperienceType | 'vcard' | 'website' | 'whatsapp' | 'wifi' | 'event'
								}
								vcardData={content.type === 'vCard' ? content.data : undefined}
								websiteUrl={content.type === 'url' ? content.data.url : undefined}
								whatsappData={
									activeExperienceType === 'whatsapp' && content.type === 'url'
										? (() => {
												const parsed = parseCreatorDestination('whatsapp', content.data.url);
												return parsed && 'phone' in parsed ? parsed : undefined;
											})()
										: undefined
								}
								wifiData={content.type === 'wifi' ? content.data : undefined}
								eventData={content.type === 'event' ? content.data : undefined}
							/>
						) : (
							<div className="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
								<ScannerPreviewFrame>
									{contentValid ? (
										<DynamicQrCode
											qrCode={{ content, config }}
											shortUrl={shortUrl || undefined}
											hasProPlan={hasProPlan}
										/>
									) : (
										<div className="flex aspect-square w-full max-w-[200px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-5 text-center text-sm text-slate-500">
											Your QR preview will appear when you enter valid content.
										</div>
									)}
								</ScannerPreviewFrame>
								<p className="mt-4 break-all text-center text-xs text-slate-500">
									{content.type === 'url' && content.data.url
										? content.data.url
										: content.type === 'vCard' && (content.data.firstName || content.data.lastName)
											? `vCard: ${[content.data.firstName, content.data.lastName].filter(Boolean).join(' ')}`
											: 'Your destination will appear here.'}
								</p>
							</div>
						)}
					</div>
				</aside>
			</div>
			{wizard.step !== 'purpose' && (
				<div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur supports-[backdrop-filter]:bg-white/85">
					<div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 pb-[calc(.75rem+env(safe-area-inset-bottom))] sm:px-6 lg:px-8">
						<Button type="button" variant="outline" onClick={wizard.back}>
							<ArrowLeftIcon className="mr-2 size-4" />
							Back
						</Button>
						{wizard.step === 'review' ? (
							wizard.experience &&
							isLandingExperience(wizard.experience) &&
							'type' in experienceContent &&
							contentValid ? (
								<PublishExperienceButton content={experienceContent as TQrExperienceContent} />
							) : wizard.experience ? (
								<CreatorReviewActions experience={wizard.experience} />
							) : null
						) : (
							<Button
								type="button"
								disabled={!wizard.canAdvance || (wizard.step === 'content' && !contentValid)}
								onClick={advance}
							>
								Continue
								<ArrowRightIcon className="ml-2 size-4" />
							</Button>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
